// Island map: a radar minimap in the HUD, and a full map you can pan (drag), zoom (wheel /
// pinch / buttons), hover, filter, and tap to walk or drive anywhere.
import { useEffect, useRef, useState } from "preact/hooks";
import { gameState, mapOpen, phoneOpen, playerLoc, world } from "../bridge";
import { DISTRICTS, FOOTPRINTS, ISLAND_RADIUS, OUTER_ZONES, RING, SPOKES, ZONES, ZONE_ICONS, type Zone } from "../../world/districts";
import { INTERIORS } from "../../world/interiors";
import { HOUSES } from "@degen/sim/houses/houses";

type Cat = "crypto" | "life" | "cars" | "houses";
const CAT_OF: Record<string, Cat> = {
  trading: "crypto", mint: "crypto", launchpad: "crypto", farm: "crypto",
  home: "life", cafe: "life", park: "life", plaza: "life", petshop: "life", dock: "life",
  polancco: "cars", abbja: "cars", mrjaay: "cars", zoomers: "cars",
  missioncontrol: "crypto", vano: "crypto", hackhub: "crypto",
  gadgets: "life", arena: "life", lighthouse: "life",
};
const catOf = (id: string): Cat => CAT_OF[id] ?? "houses";
const CAT_LABEL: Record<Cat, string> = { crypto: "📈 Crypto", life: "🏝️ Life", cars: "🚗 Cars", houses: "🏡 Houses" };

interface View {
  cx: number; // world point at the canvas center
  cz: number;
  s: number; // pixels per world unit
}

function zoneName(z: Zone): string {
  const h = HOUSES.find((x) => x.id === z.id);
  return h ? h.name : z.name;
}

function drawMap(
  c: HTMLCanvasElement,
  w: number,
  h: number,
  v: View,
  o: { labels: boolean; selected?: string | null; hover?: string | null; cats?: Set<Cat>; owned?: Set<string> },
) {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  if (c.width !== Math.round(w * dpr) || c.height !== Math.round(h * dpr)) {
    c.width = Math.round(w * dpr);
    c.height = Math.round(h * dpr);
  }
  const ctx = c.getContext("2d")!;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const X = (x: number) => w / 2 + (x - v.cx) * v.s;
  const Z = (z: number) => h / 2 + (z - v.cz) * v.s;
  ctx.fillStyle = "#2f6fae";
  ctx.fillRect(0, 0, w, h);
  const disc = (x: number, z: number, r: number, col: string) => {
    ctx.beginPath();
    ctx.arc(X(x), Z(z), r * v.s, 0, Math.PI * 2);
    ctx.fillStyle = col;
    ctx.fill();
  };
  disc(0, 0, ISLAND_RADIUS + 1, "#e8d9a8");
  disc(0, 0, ISLAND_RADIUS - 2.5, "#6fa660");
  // Roads
  ctx.strokeStyle = "#52525b";
  ctx.lineWidth = Math.max(2, (RING.outer - RING.inner) * v.s);
  ctx.beginPath();
  ctx.arc(X(0), Z(0), ((RING.inner + RING.outer) / 2) * v.s, 0, Math.PI * 2);
  ctx.stroke();
  ctx.lineWidth = Math.max(2, 5 * v.s);
  for (const sp of SPOKES) {
    ctx.beginPath();
    ctx.moveTo(X(sp.x0), Z(sp.z0));
    ctx.lineTo(X(sp.x1), Z(sp.z1));
    ctx.stroke();
  }
  for (const z of ZONES) {
    if (!OUTER_ZONES.has(z.id)) continue;
    const a = Math.atan2(z.z, z.x);
    ctx.lineWidth = Math.max(1.5, 4 * v.s);
    ctx.beginPath();
    ctx.moveTo(X(Math.cos(a) * RING.outer), Z(Math.sin(a) * RING.outer));
    ctx.lineTo(X(z.x), Z(z.z));
    ctx.stroke();
  }
  // Footpaths
  ctx.strokeStyle = "#cdbf9f";
  ctx.lineWidth = Math.max(1.5, 3 * v.s);
  for (const z of ZONES) {
    if (z.id === "plaza" || OUTER_ZONES.has(z.id)) continue;
    ctx.beginPath();
    ctx.moveTo(X(0), Z(0));
    ctx.lineTo(X(z.x), Z(z.z));
    ctx.stroke();
  }
  disc(0, 0, 13, "#d9cdb4");
  disc(DISTRICTS.park.x, DISTRICTS.park.z, 6.5, "#5b9150");
  disc(DISTRICTS.launchpad.x, DISTRICTS.launchpad.z, 5, "#52525b");
  // Dock
  ctx.fillStyle = "#8b5a2b";
  ctx.fillRect(X(DISTRICTS.dock.x - 2), Z(DISTRICTS.dock.z + 4), 4 * v.s, 16 * v.s);
  // Buildings
  for (const b of FOOTPRINTS) {
    ctx.fillStyle = o.owned?.has(b.id) ? "#86efac" : b.color;
    ctx.fillRect(X(b.x - b.w / 2), Z(b.z - b.d / 2), b.w * v.s, b.d * v.s);
  }
  // Places
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const iconSize = Math.max(11, Math.min(26, 5 * v.s));
  for (const z of ZONES) {
    if (o.cats && !o.cats.has(catOf(z.id))) continue;
    const sel = z.id === o.selected || z.id === o.hover;
    if (sel || o.owned?.has(z.id)) {
      ctx.beginPath();
      ctx.arc(X(z.x), Z(z.z), iconSize * 0.9, 0, Math.PI * 2);
      ctx.fillStyle = sel ? "rgba(124,242,154,0.5)" : "rgba(134,239,172,0.35)";
      ctx.fill();
    }
    ctx.font = `${iconSize}px system-ui`;
    ctx.fillText(ZONE_ICONS[z.id] ?? "📍", X(z.x), Z(z.z));
    // Named places always get labels; houses only once you zoom in (or hover them).
    if (o.labels && (CAT_OF[z.id] || v.s > 4 || sel)) {
      ctx.font = "700 12px system-ui";
      ctx.lineWidth = 3;
      ctx.strokeStyle = "rgba(255,255,255,0.9)";
      ctx.fillStyle = "#0d0f17";
      ctx.strokeText(zoneName(z), X(z.x), Z(z.z) + iconSize * 0.9 + 4);
      ctx.fillText(zoneName(z), X(z.x), Z(z.z) + iconSize * 0.9 + 4);
    }
  }
  return { X, Z };
}

function fitScale(w: number, h: number) {
  return Math.min(w, h) / ((ISLAND_RADIUS + 6) * 2);
}

/** Your position marker (an arrow while driving) + parked car. */
function Markers({ w, h, v }: { w: number; h: number; v: View }) {
  const loc = playerLoc.value;
  const zn = loc.inside ? ZONES.find((z) => z.id === loc.inside) : null;
  const x = zn ? zn.x : loc.x;
  const z = zn ? zn.z : loc.z;
  const px = (wx: number) => w / 2 + (wx - v.cx) * v.s;
  const pz = (wz: number) => h / 2 + (wz - v.cz) * v.s;
  return (
    <>
      {loc.car && !loc.driving && (
        <span class="map-car" style={{ left: `${px(loc.car.x)}px`, top: `${pz(loc.car.z)}px` }}>
          🚗
        </span>
      )}
      <span
        class={`map-dot ${loc.driving ? "driving" : "pulse"}`}
        style={{ left: `${px(x)}px`, top: `${pz(z)}px`, transform: loc.driving ? `rotate(${-loc.heading}rad)` : undefined }}
      />
    </>
  );
}

export function Minimap() {
  const ref = useRef<HTMLCanvasElement>(null);
  const size = 112;
  const hidden = phoneOpen.value || mapOpen.value;
  const loc = playerLoc.value;
  const v: View = { cx: loc.inside ? ZONES.find((z) => z.id === loc.inside)?.x ?? loc.x : loc.x, cz: loc.inside ? ZONES.find((z) => z.id === loc.inside)?.z ?? loc.z : loc.z, s: 1.4 };
  useEffect(() => {
    if (!hidden && ref.current) drawMap(ref.current, size, size, v, { labels: false, owned: ownedSet() });
  }, [hidden, Math.round(v.cx), Math.round(v.cz)]);
  if (hidden) return null;
  return (
    <button class="minimap" onClick={() => (mapOpen.value = true)} aria-label="Open map (M)">
      <canvas ref={ref} style={{ width: `${size}px`, height: `${size}px` }} />
      <Markers w={size} h={size} v={v} />
      <span class="minimap-n">N</span>
    </button>
  );
}

function ownedSet(): Set<string> {
  const s = gameState();
  return new Set(s ? s.houses.owned.map((h) => h.id) : []);
}

export function MapOverlay() {
  const ref = useRef<HTMLCanvasElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 360, h: 420 });
  const [view, setView] = useState<View>({ cx: 0, cz: 6, s: 2 });
  const [selected, setSelected] = useState<Zone | null>(null);
  const [hover, setHover] = useState<{ z: Zone; x: number; y: number } | null>(null);
  const [cats, setCats] = useState<Set<Cat>>(new Set(["crypto", "life", "cars", "houses"]));
  const drag = useRef<{ id: number; x: number; y: number; moved: number } | null>(null);
  const pinch = useRef<Map<number, { x: number; y: number }>>(new Map());
  const open = mapOpen.value;
  const loc = playerLoc.value;

  // Fit the island on open.
  useEffect(() => {
    if (!open) return;
    const fit = () => {
      const w = Math.min(window.innerWidth - 24, 900);
      const h = Math.min(window.innerHeight - 190, 760);
      setDims({ w, h });
      setView({ cx: 0, cz: 4, s: fitScale(w, h) });
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [open]);

  useEffect(() => {
    if (open && ref.current) drawMap(ref.current, dims.w, dims.h, view, { labels: true, selected: selected?.id, hover: hover?.z.id, cats, owned: ownedSet() });
  }, [open, dims, view, selected, hover, cats]);

  if (!open) return null;
  const fit = fitScale(dims.w, dims.h);
  const clampS = (s: number) => Math.max(fit * 0.85, Math.min(fit * 7, s));
  const toWorld = (px: number, py: number) => ({ x: view.cx + (px - dims.w / 2) / view.s, z: view.cz + (py - dims.h / 2) / view.s });
  const local = (e: { clientX: number; clientY: number }) => {
    const r = box.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const zoneAtPx = (px: number, py: number) => {
    const p = toWorld(px, py);
    const hitR = Math.max(4, 16 / view.s);
    return ZONES.filter((z) => cats.has(catOf(z.id)))
      .map((z) => ({ z, d: Math.hypot(p.x - z.x, p.z - z.z) }))
      .filter(({ z, d }) => d < (z.id === "plaza" ? Math.min(5, hitR) : hitR))
      .sort((a, b) => a.d - b.d)[0]?.z;
  };
  const zoomAt = (px: number, py: number, factor: number) => {
    const before = toWorld(px, py);
    const s = clampS(view.s * factor);
    setView({ s, cx: before.x - (px - dims.w / 2) / s, cz: before.z - (py - dims.h / 2) / s });
  };
  const go = (x: number, z: number) => {
    world()?.walkTo(x, z);
    mapOpen.value = false;
  };

  return (
    <div class="modal-backdrop map-backdrop" role="dialog" aria-modal="true" aria-label="Map">
      <div class="map-panel big">
        <div class="row-between">
          <b>🗺️ Degen Island</b>
          <div class="row" style={{ gap: "6px" }}>
            <button class="btn" onClick={() => setView({ ...view, cx: loc.x, cz: loc.z, s: clampS(fit * 3) })} aria-label="Center on me">
              📍
            </button>
            <button class="btn" onClick={() => zoomAt(dims.w / 2, dims.h / 2, 1.4)} aria-label="Zoom in">
              ＋
            </button>
            <button class="btn" onClick={() => zoomAt(dims.w / 2, dims.h / 2, 1 / 1.4)} aria-label="Zoom out">
              －
            </button>
            <button class="quest-x" aria-label="Close map" onClick={() => (mapOpen.value = false)}>
              ×
            </button>
          </div>
        </div>
        <div class="map-filters">
          {(Object.keys(CAT_LABEL) as Cat[]).map((c) => (
            <button
              key={c}
              class={`chip ${cats.has(c) ? "on" : ""}`}
              onClick={() => {
                const n = new Set(cats);
                if (n.has(c)) n.delete(c);
                else n.add(c);
                setCats(n);
              }}
            >
              {CAT_LABEL[c]}
            </button>
          ))}
        </div>
        <div
          ref={box}
          class="map-canvas"
          style={{ width: `${dims.w}px`, height: `${dims.h}px` }}
          onWheel={(e) => {
            e.preventDefault();
            const p = local(e);
            zoomAt(p.x, p.y, Math.exp(-e.deltaY * 0.0015));
          }}
          onPointerDown={(e) => {
            (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
            const p = local(e);
            pinch.current.set(e.pointerId, p);
            if (pinch.current.size === 1) drag.current = { id: e.pointerId, x: p.x, y: p.y, moved: 0 };
            else drag.current = null;
          }}
          onPointerMove={(e) => {
            const p = local(e);
            if (pinch.current.size === 2 && pinch.current.has(e.pointerId)) {
              const [a, b] = [...pinch.current.values()];
              const d0 = Math.hypot(a.x - b.x, a.y - b.y);
              pinch.current.set(e.pointerId, p);
              const [a2, b2] = [...pinch.current.values()];
              const d1 = Math.hypot(a2.x - b2.x, a2.y - b2.y);
              if (d0 > 0) zoomAt((a2.x + b2.x) / 2, (a2.y + b2.y) / 2, d1 / d0);
              return;
            }
            if (drag.current && drag.current.id === e.pointerId) {
              const dx = p.x - drag.current.x;
              const dy = p.y - drag.current.y;
              drag.current = { ...drag.current, x: p.x, y: p.y, moved: drag.current.moved + Math.abs(dx) + Math.abs(dy) };
              setView((v) => ({ ...v, cx: v.cx - dx / v.s, cz: v.cz - dy / v.s }));
              return;
            }
            if (e.pointerType === "mouse") {
              const z = zoneAtPx(p.x, p.y);
              setHover(z ? { z, x: p.x, y: p.y } : null);
            }
          }}
          onPointerUp={(e) => {
            pinch.current.delete(e.pointerId);
            const d = drag.current;
            drag.current = null;
            if (!d || d.id !== e.pointerId || d.moved > 8) return;
            const p = local(e);
            const z = zoneAtPx(p.x, p.y);
            if (z) setSelected(z);
            else {
              const wpt = toWorld(p.x, p.y);
              if (Math.hypot(wpt.x, wpt.z) < ISLAND_RADIUS - 3) go(wpt.x, wpt.z);
            }
          }}
          onPointerLeave={() => setHover(null)}
        >
          <canvas ref={ref} style={{ width: `${dims.w}px`, height: `${dims.h}px` }} />
          <Markers w={dims.w} h={dims.h} v={view} />
          {hover && (
            <span class="map-tip" style={{ left: `${hover.x + 14}px`, top: `${hover.y - 10}px` }}>
              {ZONE_ICONS[hover.z.id]} {zoneName(hover.z)}
            </span>
          )}
        </div>
        <p class="muted small" style={{ margin: 0 }}>
          {loc.inside
            ? `You're inside: ${INTERIORS[loc.inside]?.name ?? zoneName(ZONES.find((z) => z.id === loc.inside)!)}`
            : loc.driving
              ? "Tap a place to auto-drive there. Drag to pan, scroll or pinch to zoom."
              : "Tap a place to see it, or tap open ground to walk there. Drag to pan, scroll or pinch to zoom."}
        </p>
        {selected && (
          <div class="map-pick">
            <div>
              <b>
                {ZONE_ICONS[selected.id]} {zoneName(selected)}
              </b>
              <div class="muted small">{ownedSet().has(selected.id) ? "Your house 🏡" : INTERIORS[selected.id] || HOUSES.some((h) => h.id === selected.id) || CAT_OF[selected.id] === "cars" ? "You can go inside." : "Outdoor spot."}</div>
            </div>
            <div class="row" style={{ gap: "6px" }}>
              {loc.car && !loc.driving && (
                <button
                  class="btn"
                  onClick={() => {
                    world()?.walkTo(loc.car!.x, loc.car!.z);
                    mapOpen.value = false;
                  }}
                >
                  🚗 To my car
                </button>
              )}
              <button class="btn primary" onClick={() => go(selected.x, selected.z)}>
                {loc.driving ? "🚗 Drive there" : "🚶 Walk there"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
